import { Package } from "lucide-react";
import { useEffect, useState } from "react";
import type { CSSProperties } from "react";

import type { Survivor } from "../types";
import {
  motionForActivity,
  propForActivity,
  survivorInjuryIntensity,
  type SurvivorMotion,
  type SurvivorRigProp,
} from "../lib/survivorMotion";
import {
  fallbackSurvivorArt,
  figureArtModeFor,
  figureArtFor,
  rigAtlasFor,
  resolvedAppearance,
  safeArtToken,
} from "./survivorArt";
import { SurvivorRig } from "./SurvivorRig";

interface SurvivorFigureProps {
  survivor: Survivor;
  activity?: string;
  className?: string;
  decorative?: boolean;
  facing?: 1 | -1;
  lod?: "full" | "compact";
  motion?: SurvivorMotion;
  prop?: SurvivorRigProp;
}

export function SurvivorFigure({
  survivor,
  activity = "idle",
  className = "",
  decorative = true,
  facing = 1,
  lod = "full",
  motion,
  prop,
}: SurvivorFigureProps) {
  const appearance = resolvedAppearance(survivor);
  const recipe = appearance.recipe;
  const injury = survivorInjuryIntensity(survivor);
  const injured = injury > 0;
  const resolvedMotion = motion ?? motionForActivity(activity, survivor);
  const resolvedProp = prop ?? propForActivity(activity, survivor);
  const rigAtlas = rigAtlasFor(survivor);
  const requestedArt = figureArtFor(survivor, activity);
  const compactIdentityRig = lod === "compact" && figureArtModeFor(survivor) === "rig-only";
  const [source, setSource] = useState(requestedArt.source);
  const [unavailable, setUnavailable] = useState(false);
  const [rigUnavailable, setRigUnavailable] = useState(false);
  const usesRig = Boolean(rigAtlas && !rigUnavailable && (lod === "full" || compactIdentityRig));

  useEffect(() => {
    setSource(requestedArt.source);
    setUnavailable(false);
  }, [requestedArt.source]);

  useEffect(() => {
    setRigUnavailable(false);
  }, [rigAtlas]);

  const classNames = [
    "survivor-figure",
    `body-${safeArtToken(recipe.body)}`,
    `outfit-${safeArtToken(recipe.outfit)}`,
    `activity-${safeArtToken(activity)}`,
    `motion-${resolvedMotion}`,
    `pose-${requestedArt.pose}`,
    survivor.alive ? `status-${survivor.status}` : "status-dead",
    injured ? "is-injured" : "",
    survivor.equipment.length ? "has-equipment" : "",
    className,
  ].filter(Boolean).join(" ");
  const style = {
    "--survivor-phase": `${-appearance.motion_phase}ms`,
    "--figure-injury-art": `url("${fallbackSurvivorArt.figureInjuredOverlay}")`,
  } as CSSProperties;
  const accessibility = decorative
    ? { "aria-hidden": true as const }
    : { role: "img", "aria-label": `${survivor.name}, full figure` };

  function recoverFromMissingArt() {
    if (source !== fallbackSurvivorArt.figure) {
      setSource(fallbackSurvivorArt.figure);
      return;
    }
    setUnavailable(true);
  }

  return (
    <span
      className={classNames}
      style={style}
      data-activity={activity}
      data-figure-pose={requestedArt.pose}
      data-motion={resolvedMotion}
      data-appearance-profile={appearance.profile_id ?? "procedural"}
      data-renderer={usesRig ? (lod === "compact" ? "cutout-rig-compact" : "cutout-rig") : "static-raster"}
      data-survivor-status={survivor.alive ? survivor.status : "dead"}
      {...accessibility}
    >
      {usesRig ? (
        <SurvivorRig
          atlas={rigAtlas!}
          decorative
          facing={facing}
          injury={injury}
          motion={resolvedMotion}
          lod={lod}
          onAtlasError={() => setRigUnavailable(true)}
          prop={resolvedProp}
          survivor={survivor}
        />
      ) : !unavailable && (
        <img
          className="survivor-raster-figure"
          src={source}
          alt=""
          width={256}
          height={512}
          decoding="async"
          draggable={false}
          loading="lazy"
          onError={recoverFromMissingArt}
        />
      )}
      {!usesRig && (
        <span className="figure-condition-overlay" aria-hidden="true" />
      )}
      {!usesRig && survivor.equipment.length > 0 && (
        <span className="figure-equipment-mark" title={survivor.equipment.join(", ")} aria-hidden="true">
          <Package size={10} strokeWidth={2.4} />
        </span>
      )}
    </span>
  );
}
